import { css } from "@emotion/react";
import { useMemo } from "react";
import * as React from "react";
import { connect, useSelector } from "react-redux";
import { RowInfo } from "react-table";

import { DesignToken, SpacingToken } from "@livechat/design-system-react-components";

import Block from "components/layout/Block";
import { IDateRangeWithCompareFilter } from "constants/filters";
import { ReportDistribution } from "constants/reports/distribution";
import { ReportType } from "constants/reports/report-type";
import { getChartFormatter } from "helpers/chart";
import { duration } from "helpers/date";
import { useLastVisitedReport } from "hooks/reports/use-last-visited-report";
import { ReportLocalTimeLabel } from "routes/reports/components/ReportLocaltimeLabel";
import { IFirstResponseTimePerEntity } from "services/api/report/interfaces";
import { selectAgents } from "store/entities/agents";
import { selectFeatureTogglePerformanceReport } from "store/entities/license";
import { Plan, selectCurrentSubscriptionPlan } from "store/entities/subscriptions";
import { selectTeams } from "store/entities/teams";
import {
  ITicketFirstResponseTimeData,
  getDistribution,
  getFilterDateRange,
  getIsReportFetching,
  getReportData,
} from "store/views/reports";
import { withVar } from "themes/helpers";

import { ChartPanel, Filters } from "../components";
import { ChartBreakdown } from "../components/ChartBreakdown";
import { AgentCell, TeamCell, ValueCell } from "../components/PerformanceTableCells";
import { PerformanceTablePromo } from "../components/PerformanceTablePromo";
import { getChartOptions, getExportFilename } from "../helpers";
import { containerFluid } from "../styled/layout";

import { formatDurationWithFallback } from "./helpers";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type, @typescript-eslint/no-explicit-any
function pointFormatter(point: any) {
  const index = point.index !== undefined ? point.index : point.point.index;
  const count = point.series.options.extras.count && point.series.options.extras.count[index];
  const value = count > 0 ? duration(point.series.options.data[index]) : "–";
  const label = point.series.options.index === 0 ? "Time to assign to Agent" : "Time to respond";

  if (count === 0) {
    return "Number of tickets: <strong>0</strong>";
  }

  return `
    ${label}: <strong>${value}</strong><br />
    Number of tickets: <strong>${count}</strong>
  `;
}

const chartOptions = {
  yAxis: {
    labels: {
      format: "{value} h",
      // FIXME: Fix this the next time the file is edited.
      // @ts-expect-error TS(7023): 'formatter' implicitly has return type 'any' becau... Remove this comment to see the full error message
      // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
      formatter() {
        // @ts-expect-error TS(2339): Property 'value' does not exist on type '{ format:... Remove this comment to see the full error message
        return duration(this.value);
      },
    },
    reversedStacks: false,
  },
  plotOptions: {
    series: {
      stacking: "normal",
    },
  },
  tooltip: {
    formatter: getChartFormatter(pointFormatter),
  },
};

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function calculateFirstResponseTimeSeries(chartOpts: any): any {
  if (!chartOpts || !chartOpts.series[0]) {
    return chartOpts;
  }
  return {
    ...chartOpts,
    series: [
      {
        ...chartOpts.series[0],
        data: chartOpts.series[0].extras.firstResponseTimeSeconds,
      },
    ],
  };
}

interface IStateProps {
  isFetching: boolean;
  data: ITicketFirstResponseTimeData;
  distribution: ReportDistribution;
  dateRange: IDateRangeWithCompareFilter;
}

type IProps = IStateProps;

const TicketFirstResponseTime: React.FC<IProps> = React.memo(({ isFetching, data, distribution, dateRange }) => {
  useLastVisitedReport(ReportType.TicketFirstResponseTime);
  const firstResponseTimeChartLegend = useMemo(() => data && data.firstResponseTime.summary, [data]);
  const firstResponseTimeChartOptions = useMemo(
    () =>
      data &&
      getChartOptions(data.firstResponseTime.series, data.firstResponseTime.labels, distribution, null, chartOptions),
    [data, distribution]
  );
  const teams = useSelector(selectTeams);
  const agents = useSelector(selectAgents);
  const isTeam = useSelector(selectCurrentSubscriptionPlan) === Plan.Team;
  const hasPerformanceReport = useSelector(selectFeatureTogglePerformanceReport);
  const perTeamData = data?.firstResponseTimePerTeam;
  const perAgentData = data?.firstResponseTimePerAgent;
  const perTeamDataTotal = perTeamData?.find(item => item.id === "total");
  const {
    averageSecondsToAssignment: averageAssignmentPerTeam,
    averageSecondsToResponse: averageResponsePerTeam,
    total: averageTeamTotal,
  } = perTeamDataTotal ?? {};
  const perAgentDataTotal = perAgentData?.find(item => item.id === "total");
  const {
    averageSecondsToAssignment: averageAssignmentPerAgent,
    averageSecondsToResponse: averageResponsePerAgent,
    total: averageAgentTotal,
  } = perAgentDataTotal ?? {};
  const teamColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => (
          <TeamCell value={value} row={row} teamName={teams[value]?.name} totalLabel="Average" />
        ),
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableTimeToAssignHeaderStyles}></span>
            <span>Time to assign</span>
          </>
        ),
        accessor: "averageSecondsToAssignment",
        Cell: ({ value, original }: { value: number; original: IFirstResponseTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={averageAssignmentPerTeam} original={original} />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tableTimeToRespondHeaderStyles}></span>
            <span>Time to respond</span>
          </>
        ),
        accessor: "averageSecondsToResponse",
        Cell: ({ value, original }: { value: number; original: IFirstResponseTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={averageResponsePerTeam} original={original} />
        ),
        className: "even",
      },
      {
        Header: <strong>Average time</strong>,
        accessor: "total",
        Cell: ({ value, original }: { value: number; original: IFirstResponseTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={averageTeamTotal} original={original} />
        ),
        className: "even",
      },
    ];
  }, [data, teams]);

  const agentColumns = useMemo(() => {
    return [
      {
        Header: "Name",
        accessor: "id",
        Cell: ({ value, row }: { value: string; row: RowInfo["row"] }): JSX.Element => {
          const agent = agents[value];
          const { avatar, name } = agent ?? {};
          return <AgentCell value={value} row={row} agentName={name} avatar={avatar} totalLabel="Average" />;
        },
        className: "even",
      },
      {
        Header: (
          <>
            <span css={tableTimeToAssignHeaderStyles}></span>
            <span>Time to assign</span>
          </>
        ),
        accessor: "averageSecondsToAssignment",
        Cell: ({ value, original }: { value: number; original: IFirstResponseTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={averageAssignmentPerAgent} original={original} />
        ),
        className: "odd",
      },
      {
        Header: (
          <>
            <span css={tableTimeToRespondHeaderStyles}></span>
            <span>Time to respond</span>
          </>
        ),
        accessor: "averageSecondsToResponse",
        Cell: ({ value, original }: { value: number; original: IFirstResponseTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={averageResponsePerAgent} original={original} />
        ),
        className: "even",
      },
      {
        Header: <strong>Average time</strong>,
        accessor: "total",
        Cell: ({ value, original }: { value: number; original: IFirstResponseTimePerEntity }): JSX.Element => (
          <ValueCell value={value} valueToCompare={averageAgentTotal} original={original} />
        ),
        className: "even",
      },
    ];
  }, [data, teams]);

  const breakdownChartOptions = calculateFirstResponseTimeSeries(firstResponseTimeChartOptions);
  return (
    <div>
      <Filters />
      <div css={containerFluid}>
        <ChartPanel
          title="First response time"
          tooltip="Average amount of time it takes to assign a ticket to the Agent and respond to the Requester."
          loading={isFetching}
          legend={firstResponseTimeChartLegend}
          options={firstResponseTimeChartOptions}
          exportFileName={getExportFilename("ticket_first_response_time", dateRange, distribution)}
          breakdownChartOptions={breakdownChartOptions}
          breakdownValueParser={formatDurationWithFallback}
          disabledBreakdownTotal
          customComponent={
            hasPerformanceReport ? (
              <>
                <ChartBreakdown
                  customTitle="First response time per team"
                  breakdownData={{ columns: teamColumns, data: perTeamData }}
                  loading={isFetching}
                  truncateData={perTeamData && perTeamData.length > 5}
                />
                <Block marginBottom={withVar(SpacingToken.Spacing7)} />
                <ChartBreakdown
                  fallbackComponent={isTeam && <PerformanceTablePromo />}
                  customTitle="First response time per agent"
                  breakdownData={{ columns: agentColumns, data: perAgentData }}
                  loading={isFetching}
                  truncateData={perAgentData && perAgentData.length > 5}
                  disableExpand={isTeam}
                />
                <Block marginBottom={withVar(SpacingToken.Spacing7)} />
              </>
            ) : null
          }
        />
        <ReportLocalTimeLabel />
      </div>
    </div>
  );
});

TicketFirstResponseTime.displayName = "TicketFirstResponseTime";

// FIXME: Fix this the next time the file is edited.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any): IStateProps => ({
  isFetching: getIsReportFetching(state, ReportType.TicketFirstResponseTime),
  // @ts-expect-error TS(2322)
  data: getReportData(state, ReportType.TicketFirstResponseTime),
  distribution: getDistribution(state),
  dateRange: getFilterDateRange(state),
});

export default connect(mapStateToProps, null)(TicketFirstResponseTime);

const tableHeaderStyles = css`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: ${withVar(SpacingToken.Spacing1)};
  margin-top: ${withVar(SpacingToken.Spacing1)};
`;

const tableTimeToAssignHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorBlue500)};
`;

const tableTimeToRespondHeaderStyles = css`
  ${tableHeaderStyles};
  background-color: ${withVar(DesignToken.DecorGreen500)};
`;
